// TOTP multi-factor authentication (R-021) — data layer.
//
// Supabase auth MFA through the vendored supabase-js 2.110.7
// (auth.mfa.enroll / challenge / verify / challengeAndVerify / listFactors /
// unenroll / getAuthenticatorAssuranceLevel). This module owns the DECISION
// (pure, unit-tested) and the CALLS (impure, enveloped); the views own the
// pixels and never re-derive a gate.
//
// ── THE THREE RULES THIS MODULE ENFORCES ──────────────────────────────────
//
// 1. STRICTLY OPT-IN. Nothing here enrols anybody. mfaGate() returns
//    'no-factor' for an account with no verified factor, and that answer means
//    "sign in normally" — not "ask them to set it up", not "warn them". A nurse
//    who never opens the Security card must never notice this feature exists.
//    There is deliberately NO enforcement switch and no per-role requirement:
//    the moment such a flag exists, someone flips it on for "admins" and locks
//    out the founder at 22:00 on a Sunday. Enforcement, if it is ever wanted,
//    belongs in Supabase's own aal2 RLS policies where a DBA can undo it —
//    NOT in a client bundle that has to be redeployed to reverse.
//
// 2. FAIL-SAFE, NEVER FAIL-CLOSED. Every uncertain answer resolves toward
//    letting a legitimate user in, because the client-side prompt is a
//    convenience over the server's own assurance level, not the security
//    boundary. If listFactors() fails we return 'unknown' and the login path
//    proceeds — see the honest note on mfaGate(). A blank screen with a spinner
//    is not a security control; it is an outage that looks like a lockout.
//
// 3. NO HALF-AUTHENTICATED DEAD END. A wrong code leaves the session exactly as
//    it was (Supabase does not downgrade it) and the user may retry. Any other
//    failure is paired with an escape hatch in the view: sign out and start
//    again. There is no state this module can reach where the only remaining
//    action is to close the tab.
//
// ── WHAT COULD NOT BE VERIFIED HERE (read the report) ─────────────────────
// The dev harness MOCKS auth. Nothing in this file has been exercised against
// a real Supabase auth server, real TOTP secrets, or a real aal2 session. The
// pure state machine below is unit-tested exhaustively; the wire calls are
// shaped from the vendored client's own source, but only staging can prove
// them. This is flagged in dev/README.md and in the phase report as a
// user-side gate, not silently glossed.

import { getClient, ok, fail } from '../db/client.js';
import { RPCS } from '../db/contract.js';

/** The SECURITY DEFINER function the admin recovery path needs (see below). */
export const ADMIN_UNENROLL_RPC = RPCS.ADMIN_REMOVE_MFA_FACTOR;

// ─── Vocabulary ────────────────────────────────────────────────────────────

/** The four answers the login gate can get. */
export const MFA_GATE = Object.freeze({
  NO_FACTOR: 'no-factor', // sign in normally (the overwhelmingly common case)
  UNVERIFIED: 'unverified', // an abandoned half-enrolment — treated exactly as NO_FACTOR
  SATISFIED: 'satisfied', // already at aal2 (e.g. a restored session) — no prompt
  CHALLENGE: 'challenge', // verified factor + aal1 session → ask for the code
  UNKNOWN: 'unknown', // we could not find out — proceed, and say so
});

/** A TOTP code is exactly 6 digits. Whitespace is stripped (authenticator apps
 *  render "123 456" and users copy the space with it). */
export function normalizeCode(raw) {
  return String(raw === null || raw === undefined ? '' : raw).replace(/\s+/g, '');
}

/** @param {any} raw @returns {boolean} */
export function isValidCode(raw) {
  return /^\d{6}$/.test(normalizeCode(raw));
}

// ─── The pure state machine (this is the tested part) ─────────────────────

/**
 * Normalise whatever listFactors() returned into a flat array of factors.
 * Accepts the client's `{all, totp, phone, webauthn}` object, a bare array, or
 * null. Defensive on purpose: `data.totp` is pre-filtered to VERIFIED factors
 * by supabase-js, so a gate written against `totp` would silently disagree with
 * one written against `all` about a half-enrolled account. We always read
 * `all` and judge `status` ourselves.
 * @param {any} data
 * @returns {any[]}
 */
export function factorList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.all)) return data.all;
  return [];
}

/**
 * The account's verified TOTP factor, or null.
 * @param {any} data listFactors() data (or an array)
 * @returns {any|null}
 */
export function verifiedTotpFactor(data) {
  return (
    factorList(data).find(
      (f) => f && f.status === 'verified' && (f.factor_type === 'totp' || !f.factor_type)
    ) || null
  );
}

/**
 * Any TOTP factor at all, verified or not (the Security card's own view of the
 * account — an unverified factor is still something the user must be able to
 * see and clear, or a half-finished enrolment becomes an invisible splinter).
 * @param {any} data
 * @returns {any|null}
 */
export function anyTotpFactor(data) {
  return factorList(data).find((f) => f && (f.factor_type === 'totp' || !f.factor_type)) || null;
}

/**
 * THE LOGIN DECISION. Pure.
 *
 * @param {{factors?: any, factorsFailed?: boolean, aal?: {currentLevel?: string|null, nextLevel?: string|null}|null}} ctx
 * @returns {{gate: string, factorId: string|null, note: string}}
 *
 * Cases, in the order they are decided:
 *   factorsFailed        → UNKNOWN. We do not know, so we do not block. The
 *                          view says plainly that the check failed; the real
 *                          server-side assurance level is unaffected by our
 *                          ignorance, so an account that Supabase requires aal2
 *                          for will still be refused BY THE DATABASE on the
 *                          first read. That is the honest division of labour:
 *                          this prompt is UX, RLS is security.
 *   no verified factor   → NO_FACTOR (or UNVERIFIED when a half-enrolment
 *                          exists — reported separately for the Security card,
 *                          but treated identically by login: let them in).
 *   already aal2         → SATISFIED. Session restore after a completed
 *                          challenge must not re-prompt; re-prompting on every
 *                          refresh is what teaches people to hate MFA.
 *   verified + aal1      → CHALLENGE.
 */
export function mfaGate(ctx) {
  const c = ctx || {};
  if (c.factorsFailed) {
    return {
      gate: MFA_GATE.UNKNOWN,
      factorId: null,
      note: 'Could not check this account for two-step verification.',
    };
  }
  const verified = verifiedTotpFactor(c.factors);
  if (!verified) {
    const half = anyTotpFactor(c.factors);
    return {
      gate: half ? MFA_GATE.UNVERIFIED : MFA_GATE.NO_FACTOR,
      factorId: null,
      note: half
        ? 'An unfinished two-step setup exists on this account; it does not affect signing in.'
        : '',
    };
  }
  const aal = c.aal || {};
  if (aal.currentLevel === 'aal2') {
    return { gate: MFA_GATE.SATISFIED, factorId: verified.id || null, note: '' };
  }
  return { gate: MFA_GATE.CHALLENGE, factorId: verified.id || null, note: '' };
}

/**
 * Does this gate answer mean "stop and ask for a code"? The ONLY predicate the
 * login view is allowed to branch on, so a new gate value can never accidentally
 * become blocking by default.
 * @param {{gate: string}} decision
 * @returns {boolean}
 */
export function blocksLogin(decision) {
  return !!decision && decision.gate === MFA_GATE.CHALLENGE;
}

/**
 * What the 6-digit prompt IS, said plainly on the card that offers it.
 *
 * The prompt in views/login.js runs AFTER signInWithPassword() has resolved, so
 * a valid aal1 session is already in localStorage by the time it is drawn. Every
 * policy in migration 20260719120000 gates on is_active_staff()/is_admin() —
 * none require aal2 — so somebody holding only the password can still reach
 * PostgREST directly. The header of this file has always said that; the card
 * used to imply otherwise, with a green tick. §1 of the Admin view refuses to
 * paint a tick for RLS it has not probed; this is the same rule applied to the
 * card underneath it.
 */
export const MFA_SCOPE_COPY =
  'This code is asked for by the Tumaini app, and is not yet enforced by the database itself — ' +
  'it raises the bar on a shared clinic terminal, but treat your password as the thing that actually ' +
  'protects patient data, and never reuse it.';

/**
 * The Security card's own status summary. Pure copy — separated from the login
 * gate because the two answer different questions ("what must happen now?" vs
 * "what is set up on this account?").
 * @param {any} factors listFactors() data
 * @param {boolean} [failed]
 * @returns {{state: 'on'|'half'|'off'|'unknown', label: string, text: string, factorId: string|null}}
 */
export function securityStatus(factors, failed) {
  if (failed) {
    return {
      state: 'unknown',
      label: 'Could not check',
      text: 'We could not read this account’s security settings. Nothing has changed — try again in a moment.',
      factorId: null,
    };
  }
  const verified = verifiedTotpFactor(factors);
  if (verified) {
    return {
      state: 'on',
      label: 'Two-step verification is set up',
      text:
        'Signing in through Tumaini will ask for the 6-digit code from your authenticator app. ' +
        MFA_SCOPE_COPY,
      factorId: verified.id || null,
    };
  }
  const half = anyTotpFactor(factors);
  if (half) {
    return {
      state: 'half',
      label: 'Setup unfinished',
      text: 'A setup was started but never confirmed with a code, so it does nothing. Remove it and start again.',
      factorId: half.id || null,
    };
  }
  return {
    state: 'off',
    label: 'Two-step verification is off',
    text: 'Your account is protected by your password alone. Turning this on adds a 6-digit code from an authenticator app on your phone.',
    factorId: null,
  };
}

/**
 * The recovery sentence, kept in ONE place because it is a promise about
 * operations, not decoration. Tumaini has no self-service reset: there is no
 * backup-code store, no SMS fallback, and no email loop. Saying so before
 * enrolment is the difference between an inconvenience and a locked-out founder.
 */
export const MFA_RECOVERY_COPY =
  'If you lose or wipe that phone, you cannot reset this yourself — Tumaini has no self-service recovery. ' +
  'Another administrator must remove your factor from this page before you can sign in again. ' +
  'If you are the only administrator, set this up on a phone you will still have tomorrow.';

// ─── Impure wire calls (envelope everywhere; nothing throws to the caller) ──

function auth(client) {
  return ((client || getClient()).auth || {}).mfa;
}

/**
 * List this account's factors.
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function listFactors(deps) {
  try {
    const mfa = auth(deps && deps.client);
    if (!mfa || typeof mfa.listFactors !== 'function') {
      return fail(new Error('auth.mfa unavailable in this client build'), 'MFA_UNAVAILABLE');
    }
    const { data, error } = await mfa.listFactors();
    if (error) return fail(error);
    return ok(data);
  } catch (e) {
    return fail(e);
  }
}

/**
 * The session's current/next assurance level. A miss is NOT an error the caller
 * must handle — it degrades to nulls, and mfaGate() then treats an aal1-looking
 * session as needing the challenge, which is the safe-but-not-locking direction.
 * @param {{client?: any}} [deps]
 * @returns {Promise<{currentLevel: string|null, nextLevel: string|null}>}
 */
export async function assuranceLevel(deps) {
  try {
    const mfa = auth(deps && deps.client);
    if (!mfa || typeof mfa.getAuthenticatorAssuranceLevel !== 'function') return { currentLevel: null, nextLevel: null };
    const { data } = await mfa.getAuthenticatorAssuranceLevel();
    return {
      currentLevel: (data && data.currentLevel) || null,
      nextLevel: (data && data.nextLevel) || null,
    };
  } catch {
    return { currentLevel: null, nextLevel: null };
  }
}

/**
 * The whole login-time decision in one call: read the factors, read the
 * assurance level, and hand back the pure gate. Never throws.
 * @param {{client?: any}} [deps]
 * @returns {Promise<{gate: string, factorId: string|null, note: string}>}
 */
export async function evaluateLoginMfa(deps) {
  const res = await listFactors(deps);
  if (!res.ok) return mfaGate({ factorsFailed: true });
  const aal = await assuranceLevel(deps);
  return mfaGate({ factors: res.data, aal });
}

/**
 * Begin TOTP enrolment for the SIGNED-IN user (never for anyone else — the
 * client SDK has no such power, and that limit is a feature).
 * @param {string} friendlyName shown in the account's factor list
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 *   data: {id, totp: {qr_code, secret, uri}}
 */
export async function enrollTotp(friendlyName, deps) {
  try {
    const mfa = auth(deps && deps.client);
    if (!mfa || typeof mfa.enroll !== 'function') {
      return fail(new Error('auth.mfa unavailable in this client build'), 'MFA_UNAVAILABLE');
    }
    const { data, error } = await mfa.enroll({ factorType: 'totp', friendlyName });
    if (error) return fail(error);
    return ok(data);
  } catch (e) {
    return fail(e);
  }
}

/**
 * Confirm an enrolment (or answer a login challenge) with a 6-digit code.
 * challengeAndVerify() is one round trip and cannot leave a dangling challenge
 * id behind, which is the failure mode of doing challenge() and verify()
 * separately across a re-render.
 *
 * A WRONG CODE IS NOT A STATE CHANGE: Supabase rejects it and the session is
 * untouched, so the caller simply re-prompts. That property is asserted in the
 * unit tests against a stub client, because it is the difference between "try
 * again" and "you are now signed out for no reason".
 * @param {string} factorId
 * @param {string} code
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function verifyCode(factorId, code, deps) {
  const clean = normalizeCode(code);
  if (!isValidCode(clean)) {
    return fail(new Error('The code is 6 digits.'), 'MFA_BAD_CODE');
  }
  try {
    const mfa = auth(deps && deps.client);
    if (!mfa || typeof mfa.challengeAndVerify !== 'function') {
      return fail(new Error('auth.mfa unavailable in this client build'), 'MFA_UNAVAILABLE');
    }
    const { data, error } = await mfa.challengeAndVerify({ factorId, code: clean });
    if (error) return fail(error);
    return ok(data);
  } catch (e) {
    return fail(e);
  }
}

/**
 * Remove a factor from the SIGNED-IN user's own account.
 * @param {string} factorId
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function unenrollFactor(factorId, deps) {
  try {
    const mfa = auth(deps && deps.client);
    if (!mfa || typeof mfa.unenroll !== 'function') {
      return fail(new Error('auth.mfa unavailable in this client build'), 'MFA_UNAVAILABLE');
    }
    const { data, error } = await mfa.unenroll({ factorId });
    if (error) return fail(error);
    return ok(data);
  } catch (e) {
    return fail(e);
  }
}

// ─── The lockout recovery path (admin removes ANOTHER admin's factor) ──────
//
// HONESTY NOTE, and the reason this is an RPC probe rather than a function
// call: supabase-js in a browser CANNOT delete another user's MFA factor.
// auth.admin.* requires the service_role key, and putting a service_role key in
// a static bundle would hand every visitor full read/write over every patient
// row — categorically worse than the lockout it would fix. So the only correct
// client-side implementation is a SECURITY DEFINER function on the database
// that checks the caller is an admin and then deletes the factor.
//
// That function may or may not be deployed on the database this build is
// talking to. We therefore PROBE and report three states, exactly as the R-078
// RLS check does — and when it is absent we print the manual runbook rather
// than a button that pretends. A recovery path that silently does nothing is
// discovered at the worst possible moment.

/** PostgREST/Postgres codes that mean "that function is not on this database".
 *  Anything else (network, 42501 permission denied) is NOT read as absence —
 *  same rule as the R-078 probe: unknown must never masquerade as answered. */
const ABSENT_CODES = new Set(['PGRST202', '42883']);

/**
 * Classify an RPC error as "the function is absent" vs "something else went
 * wrong". Pure — this is the bit that must not drift.
 * @param {any} error
 * @returns {{absent: boolean}}
 */
export function classifyRpcAbsence(error) {
  const code = (error && (error.code || error.hint)) || '';
  const msg = String((error && error.message) || '');
  if (ABSENT_CODES.has(String(code))) return { absent: true };
  // PostgREST also words it in prose on some versions.
  if (/could not find the function|does not exist/i.test(msg) && /schema cache|function/i.test(msg)) {
    return { absent: true };
  }
  return { absent: false };
}

/**
 * Is the server-side admin removal path available on THIS database? Probed with
 * a deliberately invalid target so the call cannot remove anything even if the
 * function exists: we are asking "are you there", not "do it".
 * Three outcomes, never two — the same shape probeRlsEnforcement returns:
 *   {checked:true,  available:true }  — the function answered
 *   {checked:true,  available:false}  — the function is absent (PGRST202/42883)
 *   {checked:false, available:false}  — the probe itself failed (offline, 500,
 *                                       401, 42501): we DO NOT KNOW.
 *
 * The last branch is the one that matters. postgrest-js does not reject on a
 * transport failure — it RESOLVES with {error:{message:'TypeError: Failed to
 * fetch'}} — so treating "not absent" as "present" reported a live recovery
 * button on every offline load, which is the failure this file's own comment
 * ("unknown must never masquerade as answered") forbids.
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: {checked: boolean, available: boolean, error?: any}, error: any, code: string|null}>}
 */
export async function probeAdminUnenroll(deps) {
  try {
    const client = (deps && deps.client) || getClient();
    const { error } = await client.rpc(ADMIN_UNENROLL_RPC, { p_user_id: null });
    if (!error) return ok({ checked: true, available: true });
    if (classifyRpcAbsence(error).absent) return ok({ checked: true, available: false });
    // A null argument rejected by the function's OWN validation is proof it
    // exists — but only when the code says so. Anything else is unknown.
    if (classifyArgRejection(error).rejected) return ok({ checked: true, available: true });
    return ok({ checked: false, available: false, error: error });
  } catch (e) {
    return ok({ checked: false, available: false, error: e });
  }
}

/**
 * Did this error come from the function's own argument validation? That is
 * positive evidence the function EXISTS and we were allowed to call it:
 * PostgreSQL raises 22004 (null value not allowed), 23514 (check violation) and
 * P0001 (an explicit RAISE EXCEPTION) only after entering the function body.
 * Pure — the classification must not drift.
 * @param {any} error
 * @returns {{rejected: boolean}}
 */
export function classifyArgRejection(error) {
  const code = String((error && error.code) || '');
  return { rejected: code === '22004' || code === '23514' || code === 'P0001' };
}

/**
 * Remove another user's TOTP factor via the server-side admin path. The caller
 * MUST have already checked isAdmin() — this is defence in depth, not the gate
 * (the real gate is inside the SECURITY DEFINER function, where a hostile
 * client cannot reach around it).
 * @param {string} userId the locked-out user's auth id
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function adminRemoveFactor(userId, deps) {
  try {
    const client = (deps && deps.client) || getClient();
    const { data, error } = await client.rpc(ADMIN_UNENROLL_RPC, { p_user_id: userId });
    if (error) return fail(error, classifyRpcAbsence(error).absent ? 'MFA_ADMIN_RPC_ABSENT' : error.code);
    return ok(data);
  } catch (e) {
    return fail(e);
  }
}

/** The manual runbook shown when the server-side path is not deployed. Copy
 *  lives here so the view cannot soften it. */
export const MFA_MANUAL_RECOVERY =
  'This database does not have the admin removal function deployed, so a locked-out account cannot be recovered from this screen. ' +
  'A project owner must remove the factor in the Supabase dashboard (Authentication → Users → the user → Factors), or run ' +
  'auth.admin.mfa.deleteFactor with the service-role key from a trusted server. Tumaini deliberately does not ship a service-role key.';
